import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { PurchaseOrderDetailForm } from "@/features/purchaseOrder/form/detailForm";
import { DetailBadge } from "@/features/ui/detail/DetailBadge";
import { DetailContent } from "@/features/ui/detail/DetailContent";
import { DetailField } from "@/features/ui/detail/DetailField";
import { DetailHeader } from "@/features/ui/detail/DetailHeader";
import { DetailInformTitle } from "@/features/ui/detail/DetailInformTitle";
import { DetailLabel } from "@/features/ui/detail/DetailLabel";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { DetailLinkWrapper } from "@/features/ui/detail/DetailLinkWrapper";
import { DetailTitle } from "@/features/ui/detail/DetailTitle";
import { useUserStore } from "@/store/userStore";
import { translateWithPartnerType } from "@/utils/partnerType";
import { purchaseOrderItemStatusColor, setPurchaseOrderItemStatus } from "@/utils/scmDataStatus";
import timeUtil from "@/utils/timeUtil";
import { Checkbox, Space } from "@mantine/core";
import { AuthSignupPost201ResponseEquipmentPurchaseOrderItem } from "@sizlcorp/sizl-api-document/dist/models";
import { useTranslation } from "react-i18next";

const DetailPurchaseOrderItem = Object.assign(DetailHeader, {
  Title: DetailTitle,
  Inform: DetailInformTitle,
  Field: DetailField,
  Label: DetailLabel,
  Link: DetailLink,
  LinkWrapper: DetailLinkWrapper,
  Badge: DetailBadge,
  Content: DetailContent,
  CheckBox: Checkbox,
});

export interface DetailPurchaseOrderItemInformProps {
  data: AuthSignupPost201ResponseEquipmentPurchaseOrderItem | undefined;
  pageKey: "purchaseOrderItems" | "orderItems";
}

export const PurchaseOrderItemDetailInForm = (params: DetailPurchaseOrderItemInformProps) => {
  const { data, pageKey } = params;
  const { t } = useTranslation();
  const { openModal } = useModal();
  const { userInfo } = useUserStore();
  const partnerType = userInfo?.partner?.type;
  return (
    <>
      <DetailPurchaseOrderItem>
        <DetailPurchaseOrderItem.Title>
          {[`[${translateWithPartnerType(t, `${pageKey}.${pageKey}`, partnerType)}]`]}
        </DetailPurchaseOrderItem.Title>
        <Space h={8} />
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>
            {pageKey === "purchaseOrderItems"
              ? translateWithPartnerType(t, "purchaseOrder.code", partnerType)
              : translateWithPartnerType(t, "order.code", partnerType)}
          </DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            {data?.purchaseOrderCode}
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>
            {pageKey === "purchaseOrderItems"
              ? translateWithPartnerType(t, "purchaseOrder.name", partnerType)
              : translateWithPartnerType(t, "order.name", partnerType)}
          </DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            <DetailPurchaseOrderItem.Link
              onClick={() => {
                openModal(
                  <PurchaseOrderDetailForm purchaseOrderId={data?.purchaseOrderId ?? 0} />,
                  null,
                  t("common.detail", {
                    item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
                  })
                );
              }}
            >
              {data?.purchaseOrderName}
            </DetailPurchaseOrderItem.Link>
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>{t("partner.name")}</DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            <DetailPurchaseOrderItem.Link
              onClick={() => {
                openModal(
                  <PartnerDetailForm partnerCode={data?.partner?.code} />,
                  null,
                  t("common.detail", { item: t("partner.partner") })
                );
              }}
            >
              {data?.partner?.name}
            </DetailPurchaseOrderItem.Link>
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>
            {translateWithPartnerType(t, `${pageKey}.status`, partnerType)}
          </DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            <DetailPurchaseOrderItem.Badge
              variant="filled"
              color={
                purchaseOrderItemStatusColor[
                  data?.status as keyof typeof purchaseOrderItemStatusColor
                ]
              }
            >
              {setPurchaseOrderItemStatus(data?.status ?? "")}
            </DetailPurchaseOrderItem.Badge>
          </DetailPurchaseOrderItem.Content>
          {/* TODO: 상태 번역 */}
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>{t("item.name")}</DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            <DetailPurchaseOrderItem.Link
              onClick={() => {
                openModal(
                  <ItemsDetailForm itemCode={data?.itemCode ?? ""} />,
                  null,
                  t("common.detail", { item: t("item.item") })
                );
              }}
            >
              {data?.itemName}
            </DetailPurchaseOrderItem.Link>
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>{t("inventory.quantity")}</DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>{data?.quantity}</DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>
            {t("purchaseOrderItems.sumOfProductionPlanQuantity")}
          </DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            {data?.sumOfProductionPlanQuantity}
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>
            {translateWithPartnerType(t, `${pageKey}.sumOfStockPlanQuantity`, partnerType)}
          </DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            {data?.sumOfStockPlanQuantity}
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>
            {translateWithPartnerType(t, `${pageKey}.sumOfDeliveredQuantity`, partnerType)}
          </DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            {data?.sumOfDeliveredQuantity}
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>
            {t("stockPlan.availableStockPlanQuantity")}
          </DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            {data?.availableStockPlanQuantity}
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>
            {t("stockPlan.remainingDeliveryQuantity")}
          </DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            {data?.remainingDeliveryQuantity}
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>
            {pageKey === "purchaseOrderItems"
              ? translateWithPartnerType(t, "purchaseOrder.orderDate", partnerType)
              : translateWithPartnerType(t, "order.orderDate", partnerType)}
          </DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            {data?.orderDate === null ? "" : timeUtil(data?.orderDate ?? "")}
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>{t("order.deliveryDate")}</DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            {data?.deliveryDeadline === null ? "" : timeUtil(data?.deliveryDeadline ?? "")}
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
        <DetailPurchaseOrderItem.Field>
          <DetailPurchaseOrderItem.Label>{t("order.createdAt")}</DetailPurchaseOrderItem.Label>
          <DetailPurchaseOrderItem.Content>
            {data?.createdAt === null ? "" : timeUtil(data?.createdAt ?? "")}
          </DetailPurchaseOrderItem.Content>
        </DetailPurchaseOrderItem.Field>
      </DetailPurchaseOrderItem>
    </>
  );
};
